import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  PatchAdminV1StoresStoreIdPoliciesIdBody,
  Policy,
  PostAdminV1StoresStoreIdPoliciesBody,
} from '@nucleus-commerce/api-client/admin';

import { redirectKeys } from '@/features/redirects';

import { policiesApi } from '../api/policies-api';

import { policyKeys } from './policy-keys';

function remember(queryClient: ReturnType<typeof useQueryClient>, storeId: string, policy: Policy) {
  queryClient.setQueryData(policyKeys.detail(storeId, policy.id), policy);
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: policyKeys.list(storeId) }),
    // Renaming a published policy writes a 301 on the backend, so the redirects list is stale too.
    queryClient.invalidateQueries({ queryKey: redirectKeys.all(storeId) }),
  ]);
}

/** 409 ALREADY_EXISTS on `kind` or `handle`, 422 on `handle` for a custom policy; the form shows them. */
export function useCreatePolicy(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PostAdminV1StoresStoreIdPoliciesBody) => policiesApi.create(storeId, body),
    meta: { successMessage: 'policies:toasts.created', silentError: true },
    onSuccess: (policy) => remember(queryClient, storeId, policy),
  });
}

export function useUpdatePolicy(storeId: string, successMessage = 'policies:toasts.updated') {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: PatchAdminV1StoresStoreIdPoliciesIdBody }) =>
      policiesApi.update(storeId, id, body),
    meta: { successMessage, silentError: true },
    onSuccess: (policy) => remember(queryClient, storeId, policy),
  });
}

export function useDeletePolicy(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => policiesApi.remove(storeId, id),
    meta: { successMessage: 'policies:toasts.deleted', silentError: true },
    onSuccess: (_void, id) => {
      queryClient.removeQueries({ queryKey: policyKeys.detail(storeId, id) });
      return queryClient.invalidateQueries({ queryKey: policyKeys.list(storeId) });
    },
  });
}
